import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

export async function logAudit(orgId, {
  action,
  performedBy,
  performedByName,
  targetId = null,
  targetType = null,
  targetName = null,
  details = null,
  branchId = null,
  meta = {},
}) {
  try {
    await addDoc(collection(db, 'organisations', orgId, 'auditLog'), {
      action,
      performedBy,
      performedByName,
      targetId,
      targetType,
      targetName,
      details,
      branchId,
      meta,
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    console.error('Audit log error:', err);
  }
}

// Action constants — import these wherever you call logAudit
export const AUDIT_ACTIONS = {
  // Assets
  ASSET_CREATED: 'asset.created',
  ASSET_UPDATED: 'asset.updated',
  ASSET_DELETED: 'asset.deleted',
  ASSET_BLOCKED: 'asset.blocked',
  ASSET_UNBLOCKED: 'asset.unblocked',

  // Checks (written by Android app)
  CHECK_SUBMITTED: 'check.submitted',
  CHECK_FAILED: 'check.failed',
  CHECK_PASSED: 'check.passed',

  // Incidents
  INCIDENT_CREATED: 'incident.created',
  INCIDENT_UPDATED: 'incident.updated',
  INCIDENT_CLOSED: 'incident.closed',

  // Users
  USER_INVITED: 'user.invited',
  USER_ACTIVATED: 'user.activated',
  USER_ROLE_CHANGED: 'user.role_changed',
  USER_REMOVED: 'user.removed',
  USER_PROFILE_UPDATED: 'user.profile_updated',

  // Fuel (tank management — web)
  DERV_ORDER_PLACED: 'derv.order_placed',
  DERV_LEVEL_UPDATED: 'derv.level_updated',
  DERV_TANK_CREATED: 'derv.tank_created',
  DERV_TANK_UPDATED: 'derv.tank_updated',

  // Fuel fills (written by Android app)
  FUEL_FILL_ADDED: 'fuel.fill_added',
  FUEL_FILL_UPDATED: 'fuel.fill_updated',
  FUEL_FILL_DELETED: 'fuel.fill_deleted',

  // Templates
  TEMPLATE_CREATED: 'template.created',
  TEMPLATE_UPDATED: 'template.updated',
  TEMPLATE_DELETED: 'template.deleted',

  // Branches
  BRANCH_CREATED: 'branch.created',
  BRANCH_UPDATED: 'branch.updated',

  // Organisation
  ORG_SETTINGS_UPDATED: 'org.settings_updated',
  ORG_PLAN_CHANGED: 'org.plan_changed',

  // Staff
  STAFF_INVITED: 'staff.invited',
  STAFF_REMOVED: 'staff.removed',
  STAFF_DEPARTMENT_CHANGED: 'staff.department_changed',

  // Auth
  AUTH_LOGIN: 'auth.login',
  AUTH_LOGOUT: 'auth.logout',
};

// Human-readable labels for the audit log screen
export const AUDIT_LABELS = {
  'asset.created': 'Asset added',
  'asset.updated': 'Asset updated',
  'asset.deleted': 'Asset removed',
  'asset.blocked': 'Asset blocked',
  'asset.unblocked': 'Asset unblocked',
  'check.submitted': 'Check submitted',
  'check.failed': 'Check failed',
  'check.passed': 'Check passed',
  'incident.created': 'Incident created',
  'incident.updated': 'Incident updated',
  'incident.closed': 'Incident closed',
  'user.invited': 'User invited',
  'user.activated': 'User activated',
  'user.role_changed': 'Role changed',
  'user.removed': 'User removed',
  'user.profile_updated': 'Profile updated',
  'derv.order_placed': 'Derv order placed',
  'derv.level_updated': 'Tank level updated',
  'derv.tank_created': 'Tank added',
  'derv.tank_updated': 'Tank updated',
  'fuel.fill_added': 'Fuel fill logged',
  'fuel.fill_updated': 'Fuel fill updated',
  'fuel.fill_deleted': 'Fuel fill removed',
  'template.created': 'Template created',
  'template.updated': 'Template updated',
  'template.deleted': 'Template deleted',
  'branch.created': 'Branch added',
  'branch.updated': 'Branch updated',
  'org.settings_updated': 'Organisation settings updated',
  'org.plan_changed': 'Plan changed',
  'staff.invited': 'Staff invited',
  'staff.removed': 'Staff removed',
  'staff.department_changed': 'Department changed',
  'auth.login': 'Signed in',
  'auth.logout': 'Signed out',
};

// Colour coding for action categories on the audit screen
export const AUDIT_COLOURS = {
  'asset.blocked': { bg: 'var(--color-danger-light)', color: 'var(--color-danger)' },
  'asset.unblocked': { bg: 'var(--color-success-light)', color: 'var(--color-success)' },
  'asset.deleted': { bg: 'var(--color-danger-light)', color: 'var(--color-danger)' },
  'check.failed': { bg: 'var(--color-danger-light)', color: 'var(--color-danger)' },
  'check.passed': { bg: 'var(--color-success-light)', color: 'var(--color-success)' },
  'incident.created': { bg: 'var(--color-danger-light)', color: 'var(--color-danger)' },
  'incident.closed': { bg: 'var(--color-success-light)', color: 'var(--color-success)' },
  'user.invited': { bg: '#E6F1FB', color: '#185FA5' },
  'user.activated': { bg: 'var(--color-success-light)', color: 'var(--color-success)' },
  'user.removed': { bg: 'var(--color-danger-light)', color: 'var(--color-danger)' },
  'auth.login': { bg: '#E1F5EE', color: '#085041' },
  'auth.logout': { bg: 'var(--color-bg-secondary)', color: 'var(--color-text-tertiary)' },
};

export function getAuditColour(action) {
  return AUDIT_COLOURS[action] || { bg: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)' };
}